import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import countries from "../../../../../app/countries";
import { CustomScrollbar } from "../../customScrollbar/CustomScrollbar";

import { useGetProductsPaginatedQuery } from "../../../productAPI";
import { DatePicker } from "@mui/lab";
import { FormControl } from "@mui/material";

const schema = yup.object().shape({
  title: yup.string("Title must be text").required("Title is required"),
  description: yup.string("Description must be text"),
  productCode: yup
    .string("Product Code must be text")
    .required("Product Code is required"),
  country: yup.string(),
  //imageLink: yup.string("Image link must be text"),
  price: yup.string(),

  points: yup
    .number("Points must be a number")
    .required("Points is required")
    .integer("Points must not be decimal")
    .min(0, "Points must be greater than or equal to 0"),
  productType: yup
    .string("Product Type must be a string")
    .required("Product Type is required"),
});

const PromoPrefixField = ({ data }) => {
  let promoPrefixesString = "";

  if (data.promoPrefixes && data.promoPrefixes.length > 0) {
    for (let i = 0; i < data.promoPrefixes.length; i++) {
      promoPrefixesString += data.promoPrefixes[i].prefix;
      if (i < data.promoPrefixes.length - 1) {
        promoPrefixesString += ", ";
      }
    }
  }

  return (
    <TextField
      InputLabelProps={{ shrink: true }}
      color="primary"
      fullWidth
      label="Promo Prefixes"
      disabled
      value={promoPrefixesString}
    />
  );
};

const Form = ({ mode = "create", handleSave, currentProduct }) => {
  const { id, dataCreation, ...product } = currentProduct;
  const isEdit = mode === "edit";
  const defaultValues = {
    ...{
      title: "",
      productCode: "",
      description: "",
      price: 0,
      country: "IT",
      point: 0,
      imageLink: "",

      productType: "",
      promoPrefixes: [{ id: "", prefix: "" }],
    },
    ...product,
  };

  const form = useForm({
    defaultValues: isEdit ? currentProduct : defaultValues,
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isEdit) {
      form.reset(currentProduct);
    }
  }, [currentProduct, isEdit, form]);

  const { register, handleSubmit, formState, reset } = form;
  const { errors, isValid, isDirty } = formState;

  useEffect(() => {
    reset(defaultValues);
  }, [currentProduct]);

  const onSubmit = (data) => {
    handleSave(data);
  };

  const { data } = useGetProductsPaginatedQuery({
    activeType: "producttypeenums",
  });

  const productTypes = data?.product?.productType || [];

  return (
    <CustomScrollbar>
      <Box
        xs={12}
        id="productForm"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        maxWidth="95%"
        maxHeight="35vw"
        flexDirection={"column"}
      >
        <Grid2 container spacing={3}>
          <Grid2 xs={12}>
            <Typography variant="h3" color="primary">
              Product {isEdit ? "details" : "creation"}
            </Typography>
          </Grid2>
          <Grid2 xs={12} lg={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              disabled={isEdit}
              fullWidth
              label="Title"
              {...register("title")}
              error={!!errors.title}
              helperText={errors.title?.message}
            />
          </Grid2>
          <Grid2 xs={12} lg={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              disabled={isEdit}
              fullWidth
              label="Product Code"
              {...register("productCode")}
              error={!!errors.productCode}
              helperText={errors.productCode?.message}
            />
          </Grid2>
          <Grid2 xs={12}>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              disabled={isEdit}
              multiline
              fullWidth
              label="Description"
              {...register("description")}
              error={!!errors.description}
              helperText={errors.description?.message}
            />
          </Grid2>

          {/* COUNTRY SELECTOR */}
          <Grid2 xs={12} lg={6}>
            <FormControl fullWidth>
              {isEdit ? (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  select
                  disabled={isEdit}
                  label="Country"
                  color="primary"
                  sx={{
                    "& .MuiSelect-select": {
                      paddingTop: "13px",
                      paddingBottom: "12px",
                    },
                  }}
                  defaultValue="IT"
                  value={form.watch("country")}
                  {...register("country")}
                  error={!!errors.country}
                  helperText={errors.country?.message}
                >
                  {countries.map((ct) => (
                    <MenuItem key={ct.name} value={ct.value}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ct.icon style={{ marginRight: "10px" }} />
                        <span>{ct.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  select
                  disabled={isEdit}
                  label="Country"
                  color="primary"
                  sx={{
                    "& .MuiSelect-select": {
                      paddingTop: "13px",
                      paddingBottom: "12px",
                    },
                  }}
                  defaultValue="IT"
                  value={form.watch("country")}
                  {...register("country")}
                  error={!!errors.country}
                  helperText={errors.country?.message}
                >
                  {countries.map((ct) => (
                    <MenuItem key={ct.name} value={ct.value}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ct.icon style={{ marginRight: "10px" }} />
                        <span>{ct.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </FormControl>
          </Grid2>

          <Grid2 xs={12} lg={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              disabled={isEdit}
              label="Data Creation"
              type="date"
              color="primary"
              fullWidth
              required
              error={!!errors.dataCreation}
              helperText={errors.dataCreation?.message}
              {...register("dataCreation")}
            />
            <DatePicker />
          </Grid2>

          <Grid2 xs={12} lg={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              disabled={isEdit}
              fullWidth
              label="Price"
              {...register("price")}
              error={!!errors.price}
              helperText={errors.price?.message}
            />
          </Grid2>
          <Grid2 xs={12} lg={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              fullWidth
              label="Points"
              {...register("points")}
              error={!!errors.points}
              helperText={errors.points?.message}
            />
          </Grid2>
          <Grid2 xs={12} lg={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              disabled={isEdit}
              fullWidth
              label="Image Link"
              {...register("imageLink")}
              error={!!errors.imageLink}
              helperText={errors.imageLink?.message}
            />
          </Grid2>

          <Grid2 xs={12} lg={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              color="primary"
              fullWidth
              label="Product Type"
              {...register("productType")}
              error={!!errors.productType}
              helperText={errors.productType?.message}
              select
              value={form.getValues("productType")}
            >
              {productTypes.map((productType) => (
                <MenuItem key={productType} value={productType}>
                  <Typography variant="subtitle2">{productType}</Typography>
                </MenuItem>
              ))}
            </TextField>
          </Grid2>

          <Grid2 xs={12} lg={6}>
            <PromoPrefixField data={currentProduct} />
          </Grid2>
          <Grid2 xs={12}>
            <Tooltip arrow title={(false && "a") || ""}>
              <LoadingButton
                type="submit"
                loading={false}
                loadingPosition="start"
                startIcon={<SaveIcon />}
                variant="contained"
                sx={{
                  boxShadow: "none",
                  padding: "5px 12px 4px 12px",
                }}
                color="primary"
                onClick={() => {}}
                // disabled={isDirty && !isValid}
              >
                Save
              </LoadingButton>
            </Tooltip>
          </Grid2>
        </Grid2>
      </Box>
    </CustomScrollbar>
  );
};

export default Form;
